import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Home, ArrowLeft, Edit, Save, Plus, Coins, ToggleLeft, ToggleRight, Package
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

export default function AdminCreditPacks() {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPack, setEditingPack] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPacks();
  }, []);

  const fetchPacks = async () => {
    try {
      const response = await axios.get(`${API}/admin/credit-packs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPacks(response.data);
    } catch (error) {
      toast.error("Failed to load credit packs");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (pack) => {
    setEditingPack({ ...pack });
    setShowEditDialog(true);
  };

  const handleSave = async () => {
    if (!editingPack) return;
    
    setSaving(true);
    try {
      const payload = {
        pack_id: editingPack.pack_id,
        name: editingPack.name,
        credits: parseInt(editingPack.credits),
        price: parseFloat(editingPack.price),
        description: editingPack.description || "",
        active: editingPack.active
      };
      
      await axios.put(`${API}/admin/credit-packs/${editingPack.pack_id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Pack "${editingPack.name}" updated`);
      setShowEditDialog(false);
      setEditingPack(null);
      fetchPacks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save pack");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (pack) => {
    try {
      const payload = {
        ...pack,
        active: !pack.active
      };
      
      await axios.put(`${API}/admin/credit-packs/${pack.pack_id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Pack ${!pack.active ? 'activated' : 'deactivated'}`);
      fetchPacks();
    } catch (error) {
      toast.error("Failed to toggle pack");
    }
  };

  const pricePerCredit = (pack) => {
    return (pack.price / pack.credits).toFixed(2);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-credit-packs">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/admin")}>
              <ArrowLeft className="w-5 h-5 mr-2" /> Back to Admin
            </Button>
            <div className="h-6 w-px bg-gray-200" />
            <h1 className="font-manrope font-bold text-xl text-[#1F2933]">Credit Packs</h1>
          </div>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-manrope font-bold text-xl text-[#1F2933] hidden sm:inline">Contractor Hub LLC</span>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <p className="text-[#1F2933]/60">
            Manage credit top-up packs. Contractors can purchase these packs mid-month to add more credits.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-3 gap-6">
            {packs.map((pack) => (
              <div 
                key={pack.pack_id}
                className={`bg-white rounded-2xl p-6 border-2 transition-all ${
                  pack.active ? 'border-gray-100' : 'border-dashed border-gray-300 opacity-60'
                }`}
                data-testid={`pack-card-${pack.pack_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <Package className="w-10 h-10 text-[#0FA3B1]" />
                  <Badge className={pack.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}>
                    {pack.active ? "Active" : "Inactive"}
                  </Badge>
                </div>
                
                <div className="text-center mb-4">
                  <p className="text-4xl font-bold text-[#1F2933]">{pack.credits}</p>
                  <p className="text-[#1F2933]/50">credits</p>
                </div>
                
                <div className="text-center mb-4">
                  <p className="text-2xl font-bold text-[#0FA3B1]">${pack.price}</p>
                  <p className="text-xs text-[#1F2933]/40">${pricePerCredit(pack)}/credit</p>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleEdit(pack)}
                  >
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleActive(pack)}
                  >
                    {pack.active ? <ToggleRight className="w-5 h-5 text-green-500" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Credit Pack</DialogTitle>
          </DialogHeader>
          
          {editingPack && (
            <div className="space-y-4 pt-4">
              <div>
                <Label>Pack Name</Label>
                <Input
                  value={editingPack.name}
                  onChange={(e) => setEditingPack({...editingPack, name: e.target.value})}
                  className="mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Credits</Label>
                  <Input
                    type="number"
                    value={editingPack.credits}
                    onChange={(e) => setEditingPack({...editingPack, credits: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Price ($)</Label>
                  <Input
                    type="number"
                    value={editingPack.price}
                    onChange={(e) => setEditingPack({...editingPack, price: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>
              
              {editingPack.credits > 0 && editingPack.price > 0 && (
                <p className="text-sm text-[#1F2933]/60 text-center">
                  = ${(editingPack.price / editingPack.credits).toFixed(2)} per credit
                </p>
              )}
              
              <div className="flex gap-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-[#0FA3B1]" 
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? <div className="spinner w-4 h-4 border-2 border-white/20 border-t-white"></div> : <><Save className="w-4 h-4 mr-1" /> Save</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
